import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentPreview } from '@/components/AttachmentPreview';
import { TileModal } from '@/components/Dashboard/TileModal';
import { ScheduledTestCard } from '@/components/ScheduledTestsModal/ScheduledTestCard';
import { buildScheduledTestsItems } from '@/components/ScheduledTestsModal/buildListItems';
import { useTestSchedules } from '@/components/ScheduledTestsModal/hooks/useTestSchedules';
import { seasonBanners } from '@/components/ScheduledTestsModal/season';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import type { Attachment } from '@/types/attachment';
import { ScheduledTestsItemType } from '@/types/components';
import { formatDateWithWeekday, formatMonth } from '@/utils/date';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ScheduledTestsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useTestSchedules(isOpen);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );

  const items = useMemo(
    () => buildScheduledTestsItems(data ?? [], new Date()),
    [data],
  );

  return (
    <>
      <TileModal
        isOpen={isOpen && !previewAttachment}
        title={t('diary.testScheduleTitle')}
        hideBorder
        autoHideScrollbar
        onClose={onClose}
      >
        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <PageSpinner className="text-subtle-foreground size-[22px]" />
          </div>
        )}

        {!isLoading && (
          <div className="flex flex-col pb-6">
            {items.map((item, index) => {
              switch (item.type) {
                case ScheduledTestsItemType.SeasonHeader: {
                  const banner = seasonBanners[item.season];

                  return (
                    <React.Fragment key={index}>
                      <img
                        src={banner.mobile}
                        alt=""
                        className="mb-4 block w-full md:hidden"
                      />
                      <img
                        src={banner.web}
                        alt=""
                        className="mb-4 hidden w-full md:block"
                      />
                    </React.Fragment>
                  );
                }
                case ScheduledTestsItemType.MonthHeader:
                  return (
                    <h3
                      key={index}
                      className="text-title-24 md:text-callout-16 px-4 pt-4 pb-2 text-foreground capitalize md:px-5"
                    >
                      {formatMonth(item.date.toISOString())}
                    </h3>
                  );
                case ScheduledTestsItemType.DayHeader:
                  return (
                    <p
                      key={index}
                      className="text-subhead-14 text-subtle-foreground px-4 py-2 md:px-5"
                    >
                      {formatDateWithWeekday(item.date)}
                    </p>
                  );
                case ScheduledTestsItemType.Test:
                  return (
                    <ScheduledTestCard
                      key={index}
                      test={item.test}
                      onPreview={setPreviewAttachment}
                    />
                  );
                case ScheduledTestsItemType.EmptyMonth:
                  return (
                    <p
                      key={index}
                      className="text-body-14 text-subtle-foreground px-4 py-6 text-center md:px-5"
                    >
                      {t('diary.emptyTests')}
                    </p>
                  );
                default: {
                  const exhaustiveCheck: never = item;

                  return exhaustiveCheck;
                }
              }
            })}
          </div>
        )}
      </TileModal>

      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          onDownloaded={() => setPreviewAttachment(null)}
        />
      )}
    </>
  );
};
